// /admin/compliance/queue — operator KYC review queue (portal-kyc Phase E).
//
// Data source (PLAN §6b): GET /api/admin/compliance/queue?status=&country=&q=
//   → { clients: QueueRow[] } where each row carries client_id, client_name,
//     country, entity_type, rollup, counts{uploaded,in_review,rejected},
//     oldest_in_review_at, latest_submitted_at.
//
// The server already sorts (clients with rejected docs first, then oldest
// in_review ascending) — we preserve that order and do NOT re-sort here.
//
// Filters (status / country / q) are driven by URL query params via
// useSearchParams so a deep-linked / reloaded URL restores the same view
// (mirrors the /admin/support precedent — admin_ux url_params).
//
// Chips render inline (support-cx precedent #7 — StatusChip is NOT extended).
// All copy here is operational language, not regulatory prose (REQ-008).

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getKycStatusLabel, type KycStatus } from '@shared';
import { apiGet, ApiError } from '../../lib/api';
import { ComplianceTabs } from './ComplianceTabs';

interface QueueRow {
  client_id: string;
  client_name: string;
  country: string;
  entity_type: string;
  rollup: KycStatus;
  counts: { uploaded: number; in_review: number; rejected: number };
  oldest_in_review_at: string | null;
  latest_submitted_at: string | null;
}

type StatusFilter = 'all' | 'in_review' | 'rejected';

// Operational chip tone for the rollup status (mirrors Clients.tsx KYC_TONE).
function rollupChipTone(s: KycStatus): string {
  switch (s) {
    case 'current': return 'green';
    case 'in_review': return 'accent';
    case 'rejected': return 'red';
    case 'expired': return 'red';
    case 'refresh': return 'amber';
    case 'pending': default: return '';
  }
}

// Relative-time formatter for the oldest in_review timestamp (SLA aging
// visibility). Falls back to an em dash when no in_review doc exists.
function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function countsLabel(counts: QueueRow['counts']): string {
  const parts: string[] = [];
  if (counts.in_review > 0) parts.push(`${counts.in_review} in review`);
  if (counts.rejected > 0) parts.push(`${counts.rejected} rejected`);
  if (counts.uploaded > 0) parts.push(`${counts.uploaded} draft`);
  return parts.length ? parts.join(', ') : '—';
}

export function ComplianceQueue() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state is derived from the URL so deep-link reload restores it.
  const statusFilter = (searchParams.get('status') as StatusFilter) || 'all';
  const countryFilter = searchParams.get('country') ?? '';
  const q = searchParams.get('q') ?? '';

  const [rows, setRows] = useState<QueueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    // Build the query the server understands. Empty country/q are omitted; the
    // text search is applied server-side (case-insensitive name substring).
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (countryFilter) params.set('country', countryFilter);
    if (q.trim()) params.set('q', q.trim());
    const suffix = params.toString() ? `?${params.toString()}` : '';

    apiGet<{ clients: QueueRow[] }>(`/api/admin/compliance/queue${suffix}`)
      .then((r) => { if (!cancelled) setRows(r.clients); })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.serverMessage : 'Could not load the compliance queue.');
      });
    return () => { cancelled = true; };
  }, [statusFilter, countryFilter, q]);

  // Distinct country list for the dropdown — derived from the loaded rows.
  // (The server returns the full set for the active status filter, so this is
  // a reasonable approximation; an unmatched stored country still round-trips
  // through the URL.)
  const countries = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows ?? []) set.add(r.country);
    if (countryFilter) set.add(countryFilter);
    return Array.from(set).sort();
  }, [rows, countryFilter]);

  function patchParams(next: Record<string, string>) {
    const merged = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    setSearchParams(merged, { replace: true });
  }

  return (
    <>
      <ComplianceTabs />
      <div className="admin-page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <h1 className="portal-h1">KYC Compliance</h1>
          <p className="portal-sub">
            Clients with KYC documents awaiting review. Rejected items appear first, then the
            oldest submissions under review — open a client to accept, reject, or finalise.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="tabs">
          {(['all', 'in_review', 'rejected'] as StatusFilter[]).map((t) => (
            <button
              key={t}
              className={statusFilter === t ? 'on' : ''}
              onClick={() => patchParams({ status: t === 'all' ? '' : t })}
            >
              {t === 'all' ? 'All' : t === 'in_review' ? 'In review' : 'Rejected'}
            </button>
          ))}
        </div>

        <select
          value={countryFilter}
          onChange={(e) => patchParams({ country: e.target.value })}
          style={{ minWidth: 140 }}
          aria-label="Filter by country"
        >
          <option value="">All countries</option>
          {countries.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <input
          type="search"
          value={q}
          onChange={(e) => patchParams({ q: e.target.value })}
          placeholder="Search client name…"
          aria-label="Search client name"
          style={{ minWidth: 200, flex: '1 1 200px' }}
        />
      </div>

      <div className="card flush" style={{ marginTop: 16 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Client</th>
              <th>Type / country</th>
              <th>Status</th>
              <th>Documents</th>
              <th>Oldest in review</th>
              <th className="amt" />
            </tr>
          </thead>
          <tbody>
            {rows === null && !error && (
              <tr><td colSpan={6} style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</td></tr>
            )}
            {error && (
              <tr><td colSpan={6} style={{ color: 'var(--red)', fontSize: 13 }}>{error}</td></tr>
            )}
            {rows !== null && !error && rows.length === 0 && (
              <tr>
                <td colSpan={6} style={{ color: 'var(--fg-mute)', fontSize: 13, textAlign: 'center', padding: 32 }}>
                  No KYC submissions awaiting review.
                </td>
              </tr>
            )}
            {(rows ?? []).map((r) => (
              <tr key={r.client_id} className="row-clickable">
                <td>
                  <Link
                    to={`/admin/compliance/clients/${r.client_id}`}
                    style={{ fontWeight: 500, color: 'var(--fg)', textDecoration: 'none' }}
                  >
                    {r.client_name}
                  </Link>
                  <div style={{ fontSize: 12, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                    {r.client_id}
                  </div>
                </td>
                <td style={{ color: 'var(--fg-soft)', fontSize: 13 }}>
                  {r.entity_type} · {r.country}
                </td>
                <td>
                  <span className={`chip ${rollupChipTone(r.rollup)}`}>
                    {getKycStatusLabel(r.rollup, 'staff')}
                  </span>
                </td>
                <td style={{ color: 'var(--fg-soft)', fontSize: 13 }}>{countsLabel(r.counts)}</td>
                <td style={{ color: 'var(--fg-soft)', fontSize: 13 }}>{relativeTime(r.oldest_in_review_at)}</td>
                <td className="amt">
                  <Link to={`/admin/compliance/clients/${r.client_id}`} className="btn sm">Review →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
